import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dest = location.state?.from || "/dashboard";
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (user) navigate(dest, { replace: true }); }, [user, navigate, dest]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await login(form.email, form.password);
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally { setBusy(false); }
  };

  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-rice">
      <div className="hidden lg:block grain relative">
        <img src="https://images.pexels.com/photos/6549849/pexels-photo-6549849.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/60" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-rice">
          <Link to="/" className="flex items-center gap-3"><span className="w-10 h-10 bg-cinnabar flex items-center justify-center font-serif text-2xl">中</span><span className="font-serif text-2xl">CCLPT</span></Link>
          <div>
            <p className="font-serif text-4xl leading-tight">欢迎回来</p>
            <p className="text-rice/60 mt-2">Welcome back to your learning journey.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8"><span className="w-9 h-9 bg-cinnabar text-rice flex items-center justify-center font-serif text-xl">中</span><span className="font-serif text-xl">CCLPT</span></Link>
          <h1 className="font-serif text-4xl">Log in</h1>
          <p className="text-clay mt-2 mb-8 text-sm">Access your student or teacher dashboard.</p>

          {error && <div className="mb-4 px-4 py-3 bg-cinnabar/10 border border-cinnabar/30 text-cinnabar text-sm" data-testid="login-error">{error}</div>}

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="label text-clay">Email</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="login-email" className="w-full mt-1.5 px-4 py-3 border border-black/10 bg-white focus:outline-none focus:ring-1 focus:ring-cinnabar" />
            </div>
            <div>
              <label className="label text-clay">Password</label>
              <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="login-password" className="w-full mt-1.5 px-4 py-3 border border-black/10 bg-white focus:outline-none focus:ring-1 focus:ring-cinnabar" />
            </div>
            <button disabled={busy} data-testid="login-submit"
              className="w-full py-3.5 bg-ink text-rice font-medium hover:bg-cinnabar transition-colors duration-300 disabled:opacity-60">
              {busy ? "Signing in…" : "Log in"}
            </button>
          </form>

          <div className="flex items-center gap-3 my-6"><div className="flex-1 h-px bg-black/10" /><span className="text-xs text-clay">OR</span><div className="flex-1 h-px bg-black/10" /></div>

          <button onClick={googleLogin} data-testid="login-google"
            className="w-full py-3.5 border border-black/15 bg-white font-medium hover:border-cinnabar transition-colors flex items-center justify-center gap-3">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" /> Continue with Google
          </button>

          <p className="text-sm text-clay mt-8 text-center">
            New here? <Link to="/register" className="text-cinnabar font-medium hover:underline">Create an account</Link>
          </p>
          <div className="mt-6 text-xs text-clay/70 bg-white border border-black/10 p-3 font-mono">
            Demo: student@cclpt.com / Student@2026 · teacher@cclpt.com / Teacher@2026
          </div>
        </div>
      </div>
    </div>
  );
}
